import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MONO, SANS, SERIF } from "./brand";

/* The Monday one pager: the real weekly memo, as a page a prospect can open. */

const INK = "#1B2434";
const SOFT = "#66707F";
const LINE = "#E6DFD2";
const GOOD = "#2E8F62";
const WARN = "#A8781F";

const METRICS = [
  ["Revenue", "$248,100", "+6.2% vs prior week", GOOD],
  ["Blended CAC", "$54.20", "+11.8%, watch", WARN],
  ["CVR, product page", "3.42%", "+3.4%, test win shipped", GOOD],
  ["Email revenue share", "28.4%", "+1.9%", GOOD],
];

const SHIPPED = [
  ["#4C9AFF", "Meta", "50 new ads launched. Ad #012 beat every live ad by 15%, budget tripled."],
  ["#FFC46B", "Google", "Bids and budgets re-tuned overnight on order data. Four zero order search terms blocked."],
  ["#A78BFA", "Klaviyo", "Winback subject line called at 48.9% opens. The loser is gone, a new challenger is sending."],
  ["#5FE3A1", "CRO", "Routine builder page test won and shipped, +3.4% orders. The miss on the cart banner reverted."],
  ["#FF8FA3", "Creative", "New advertorial live for the buyers who need convincing. Hooks pulled from 1,571 reviews."],
  ["#7DD3FC", "Reporting", "47 actions this week, each on the record with a diff and an undo. Two are waiting on you below."],
];

const NEEDS = [
  ["Raise weekly ad spend by $6,000", "Ad #012 holds CPA 15% under target at three times the budget. Real money, your call."],
  ["Repeat buyers dipped 0.4 points", "Two theories, subscription timing or the day 28 email. Pick which one to fund."],
];

function Sheet() {
  return (
    <div
      className="mx-auto w-full max-w-[860px] rounded-lg"
      style={{ background: "#FDFBF6", color: INK, boxShadow: "0 30px 70px rgba(20,24,32,0.22)" }}
      data-testid="report-sheet"
    >
      {/* masthead */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-5 sm:px-9" style={{ borderColor: LINE }}>
        <span className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded-[4px]" style={{ background: "#5FE3A1" }} />
          <span className="text-[15px] font-bold tracking-[-0.01em]" style={{ fontFamily: SANS }}>Axos</span>
        </span>
        <span className="hidden h-4 w-px sm:block" style={{ background: LINE }} />
        <span className="text-[10px] uppercase tracking-[0.2em]" style={{ fontFamily: MONO, color: SOFT }}>
          Weekly operator report
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: SOFT }} data-testid="report-week">
          Week 23 · Monday 06:00
        </span>
      </div>

      {/* topline */}
      <div className="px-6 pt-7 sm:px-9" data-testid="report-topline">
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: GOOD }}>
          The number that pays for everything
        </div>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-[46px] font-semibold leading-none tracking-[-0.02em] sm:text-[58px]" style={{ fontFamily: SERIF }}>
            $248,100
          </span>
          <span className="text-[15px] font-semibold" style={{ color: GOOD }}>+6.2% week over week</span>
        </div>
        <p className="mt-3 max-w-[62ch] text-[13.5px] leading-[1.75]" style={{ color: SOFT }}>
          Built from five sources on the same definitions, so nobody argues about which revenue number is real.
          Everything below already happened. Two decisions are waiting on you, everything else is done.
        </p>
      </div>

      {/* metric grid */}
      <div className="grid gap-3 px-6 pt-6 sm:grid-cols-4 sm:px-9" data-testid="report-metrics">
        {METRICS.map(([l, v, d, c]) => (
          <div key={l} className="rounded-md border p-3.5" style={{ borderColor: LINE, background: "#FFFFFF" }}>
            <div className="text-[8.5px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: SOFT }}>{l}</div>
            <div className="mt-1.5 text-[21px] font-bold leading-none tracking-[-0.01em]" style={{ fontFamily: SANS }}>{v}</div>
            <div className="mt-1.5 text-[10px] leading-snug" style={{ fontFamily: MONO, color: c }}>{d}</div>
          </div>
        ))}
      </div>

      {/* what shipped */}
      <div className="px-6 pt-8 sm:px-9" data-testid="report-shipped">
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: SOFT }}>
          What shipped, machine by machine
        </div>
        <div className="mt-3 overflow-hidden rounded-md border" style={{ borderColor: LINE, background: "#FFFFFF" }}>
          {SHIPPED.map(([c, name, line], i) => (
            <div key={name} className="flex items-start gap-3.5 px-4 py-3" style={{ borderTop: i ? `1px solid ${LINE}` : "none" }}>
              <span className="mt-[5px] h-2 w-2 shrink-0 rounded-full" style={{ background: c }} />
              <span className="w-[76px] shrink-0 pt-[1px] text-[9px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: SOFT }}>
                {name}
              </span>
              <span className="min-w-0 flex-1 text-[13px] leading-[1.65]">{line}</span>
            </div>
          ))}
        </div>
      </div>

      {/* needs you */}
      <div className="px-6 pt-7 sm:px-9" data-testid="report-needs-you">
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: WARN }}>
          Needs you Monday · 2 decisions
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {NEEDS.map(([t, d]) => (
            <div key={t} className="rounded-md border p-4" style={{ borderColor: "#E4CFA4", background: "#FBF4E4" }}>
              <div className="text-[13.5px] font-bold leading-snug" style={{ fontFamily: SANS }}>{t}</div>
              <p className="mt-1.5 text-[12px] leading-[1.7]" style={{ color: SOFT }}>{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* record */}
      <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t px-6 py-4 sm:px-9" style={{ borderColor: LINE }} data-testid="report-record">
        <span className="text-[9px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: GOOD }}>47 actions taken</span>
        <span className="text-[9px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: WARN }}>2 waiting on a person</span>
        <span className="text-[9px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: SOFT }}>every one has a diff and an undo · RUN-4417</span>
        <span className="ml-auto text-[9px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: SOFT }}>page 1 of 1</span>
      </div>
    </div>
  );
}

export default function AxosReport() {
  const [sp] = useSearchParams();
  const embed = sp.get("embed") === "1";

  if (embed) {
    return (
      <div className="min-h-screen px-3 py-4" style={{ background: "#EEE8DC", fontFamily: SANS }} data-testid="report-embed">
        <Sheet />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#EEE8DC", fontFamily: SANS }} data-testid="report-page">
      <div
        className="fixed left-0 right-0 top-0 z-[90] flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-2.5 sm:px-6"
        style={{ background: "rgba(8,9,11,0.92)", borderColor: "#1E2530", backdropFilter: "blur(14px)" }}
      >
        <Link to="/" data-testid="report-back" className="text-[10px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: "#5FE3A1" }}>
          ← The machine
        </Link>
        <span className="text-[10px] uppercase tracking-[0.14em]" style={{ fontFamily: MONO, color: "#5F6B7A" }}>
          Reporting machine · the Monday one pager
        </span>
        <span className="ml-auto hidden text-[10px] uppercase tracking-[0.14em] sm:inline" style={{ fontFamily: MONO, color: "#5F6B7A" }}>
          also lands as PDF, Slack, email, dashboard or slides
        </span>
      </div>
      <div className="px-4 pb-16 pt-[84px]">
        <Sheet />
      </div>
    </div>
  );
}
